import { useContext, useEffect, useMemo, useState } from 'react';
import Header from 'widgets/header';
import { useRouter } from 'next/router';
import { newPlanUrls } from 'utils/api/urls';
import LegalDocumentTnc from 'widgets/tnc';
import { MPosContext } from 'utils/context';
import axios from 'axios';
import { getAxiosConfig, getDomainOrigin } from 'utils/serverConfig';
import {
    pageEvent,
    trackEvent,
} from '@acko-tech/building-blocks.ui.acko-analytics';
import {
    fetchFinancialDetailsForm,
    submitFinancialDetails,
} from 'utils/api/services';
import { ModalMobile, ModalDesktop } from '@acko-tech/building-blocks.ui.modal';
import { PrimaryButton } from '@acko-tech/building-blocks.ui.button';
import { useViewport } from 'utils/responsive';
import { LegalDocumentsSegments } from './constants';
import FinancialDetailsForm from './FinancialDetailsForm';
import * as Styles from './styles';
import { FinancialFormValues, IFinancialFieldConfig } from './types';
import {
    applySyncGroupValue,
    buildSubmitPayload,
    clearDraftStorage,
    flattenFinancialData,
    getLobPrefix,
    hasAllRequiredValues,
    loadDraftFromStorage,
    mergeDraftValues,
    saveDraftToStorage,
    validateAllFields,
    validateField,
} from './helpers';

function LegalDocumentsUpload() {
    const router = useRouter();
    const { isMobile } = useViewport();
    const Modal: any = isMobile ? ModalMobile : ModalDesktop;
    const {
        partnerId,
        legalDetailsUploadedStatus,
        setSnackbar,
        setLegalDetailsUploadedStatus,
        userDetails,
        permissions,
    } = useContext(MPosContext);

    const groupName = permissions.group;
    const [uiConfig, setUiConfig] = useState<IFinancialFieldConfig[]>([]);
    const [description, setDescription] = useState(
        'Please provide the below details to enable Acko to process timely protection plan commission payouts'
    );
    const [buttonName, setButtonName] = useState('Submit');
    const [mode, setMode] = useState<'capture' | 'confirm'>('capture');
    const [values, setValues] = useState<FinancialFormValues>({});
    const [validationErr, setValidationErr] = useState<Record<string, string>>(
        {}
    );
    const [isLoading, setIsLoading] = useState(true);
    const [isBtnLoading, setIsBtnLoading] = useState(false);
    const [isFetchingIfsc, setIsFetchingIfsc] = useState(false);
    const [isValidIfsc, setIsValidIfsc] = useState(false);
    const [showTnc, setShowTnc] = useState(false);
    const [isRendered, setIsRendered] = useState(false);
    const [warningMessage, setWarningMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [ctaPressed, setCtaPressed] = useState(false);
    // After partial payout match: stay on page with warning until user taps Continue.
    const [awaitingPartialContinue, setAwaitingPartialContinue] =
        useState(false);
    const validationConfig =
        mode === 'confirm'
            ? uiConfig.filter((field) => field.component !== 'tnc_checkbox')
            : uiConfig;

    useEffect(() => {
        pageEvent(LegalDocumentsSegments.VIEW_PAGE_DEALER_DETAILS_COLLECTION, {
            page: 'legalBankDocuments',
            partner: partnerId,
            product: 'electronics',
            sub_product_name: 'pos',
            phone: userDetails.phone,
            city_name: userDetails.pincode,
            unique_id: userDetails.user_id,
            platform: 'mweb',
        });
    }, []);

    useEffect(() => {
        const loadForm = async () => {
            if (!partnerId || !groupName) return;
            setIsLoading(true);
            const response = await fetchFinancialDetailsForm(
                partnerId,
                groupName
            );
            if (response.statusCode === 401) {
                router.push('/login');
                return;
            }
            if (response.is_error || !response.data) {
                setSnackbar({
                    type: 'failure',
                    message:
                        response.error_message ||
                        'Failed to load financial details',
                });
                setIsLoading(false);
                return;
            }

            const form = response.data;
            const config = (form.ui_config || []) as IFinancialFieldConfig[];
            setUiConfig(config);
            setDescription(
                form.description ||
                    'Please provide the below details to enable Acko to process timely protection plan commission payouts'
            );

            const isConfirm =
                !!form.confirmation_required &&
                !form.details_update_required &&
                !!form.data;
            setMode(isConfirm ? 'confirm' : 'capture');
            setButtonName(
                form.button_name || (isConfirm ? 'Confirm' : 'Submit')
            );

            const flattened = flattenFinancialData(
                form.data,
                config,
                form.partner_name
            );
            const draft = isConfirm
                ? null
                : loadDraftFromStorage(partnerId, groupName);
            const nextValues = mergeDraftValues(flattened, draft);
            if (form.partner_name) {
                const dealershipField = config.find(
                    (f) => f.props?.key_name === 'dealership_name'
                );
                const beneficiaryField = config.find(
                    (f) => f.props?.key_name === 'beneficiary_name'
                );
                const dealershipEditable =
                    dealershipField &&
                    'editable' in (dealershipField.props || {}) &&
                    dealershipField.props.editable === true;
                const beneficiaryEditable =
                    beneficiaryField &&
                    'editable' in (beneficiaryField.props || {}) &&
                    beneficiaryField.props.editable === true;
                if (!dealershipEditable) {
                    nextValues.dealership_name = form.partner_name;
                }
                if (!beneficiaryEditable) {
                    nextValues.beneficiary_name = form.partner_name;
                }
            }
            setValues(nextValues);

            if (
                nextValues.ifsc_code &&
                String(nextValues.ifsc_code).length > 4
            ) {
                ifscValidation(String(nextValues.ifsc_code));
            } else if (isConfirm && nextValues.ifsc_code) {
                setIsValidIfsc(true);
            }

            setIsRendered(true);
            setIsLoading(false);
        };
        loadForm();
    }, [partnerId, groupName]);

    useEffect(() => {
        if (isRendered && mode === 'capture' && partnerId && groupName) {
            saveDraftToStorage(partnerId, groupName, values);
        }
    }, [values, isRendered, mode, partnerId, groupName]);

    const isBtnDisabled = useMemo(() => {
        if (!uiConfig.length) return true;
        return !hasAllRequiredValues(validationConfig, values, isValidIfsc);
    }, [uiConfig, validationConfig, values, isValidIfsc]);

    const ifscValidation = async (ifscCode: string) => {
        setIsFetchingIfsc(true);
        try {
            const { data, status } = await axios({
                method: 'GET',
                url: `${getDomainOrigin()}/api/mpos/ifsc/${ifscCode}`,
                ...getAxiosConfig(),
            });
            if (status === 200 && data?.success) {
                setIsValidIfsc(true);
                setValidationErr((prev) => ({ ...prev, ifsc_code: '' }));
            } else {
                setIsValidIfsc(false);
                setValidationErr((prev) => ({
                    ...prev,
                    ifsc_code: 'Please enter a valid IFSC code',
                }));
            }
        } catch {
            setIsValidIfsc(true);
            setValidationErr((prev) => ({
                ...prev,
                ifsc_code: 'Please enter a valid IFSC code',
            }));
        } finally {
            setIsFetchingIfsc(false);
        }
    };

    const onChangeValue = (key: string, value: any) => {
        setErrorMessage('');
        setValues((prev) => {
            const next = applySyncGroupValue(uiConfig, prev, key, value);
            const field = uiConfig.find((f) => f.props.key_name === key);
            if (field) {
                const error = validateField(field, next);
                setValidationErr((prevErr) => ({
                    ...prevErr,
                    [key]: error,
                }));
            }
            if (key === 'account_number' && prev.confirm_account_number) {
                const confirmField = uiConfig.find(
                    (f) => f.props.key_name === 'confirm_account_number'
                );
                if (confirmField) {
                    const confirmError = validateField(confirmField, next);
                    setValidationErr((prevErr) => ({
                        ...prevErr,
                        confirm_account_number: confirmError,
                    }));
                }
            }
            return next;
        });
    };

    const onBlurField = (field: IFinancialFieldConfig) => {
        const key = field.props.key_name;
        if (key === 'ifsc_code' && values.ifsc_code?.length > 4) {
            ifscValidation(String(values.ifsc_code));
        }
        const error = validateField(field, values);
        setValidationErr((prev) => ({ ...prev, [key]: error }));
    };

    const onSelectFile = (
        id: string,
        file: File,
        field: IFinancialFieldConfig
    ) => {
        const key = field.props.key_name;
        const documentType = field.props.document_type || 'PAN_IMAGE';
        setValues((prev) => ({
            ...prev,
            [key]: {
                name: id,
                uploadStatus: 'uploading',
                url: prev[key]?.url || '',
            },
        }));
        import('utils/api/services').then(async (req) => {
            const url = `${getDomainOrigin()}/${newPlanUrls.documentUpload}`;
            const { data, statusCode, status } = await req.uploadDocument(
                url,
                file,
                documentType,
                userDetails.user_id,
                () => {}
            );
            if (statusCode === 401) {
                router.push('/login');
                return;
            }
            if (status && data) {
                setValues((prev) => ({
                    ...prev,
                    [key]: {
                        name: id,
                        uploadStatus: 'uploaded',
                        url: data.url,
                    },
                }));
                setValidationErr((prev) => ({ ...prev, [key]: '' }));
                return;
            }
            setValues((prev) => ({
                ...prev,
                [key]: {
                    name: id,
                    uploadStatus: 'failed',
                    url: '',
                },
            }));
        });
    };

    const onDeleteFile = (field: IFinancialFieldConfig) => {
        const key = field.props.key_name;
        setValues((prev) => ({
            ...prev,
            [key]: { name: '', uploadStatus: '', url: '' },
        }));
    };

    const redirectToDashboard = () => {
        const lobPrefix = getLobPrefix(partnerId, permissions);
        router.push(`/${lobPrefix}/${partnerId}/dashboard`);
    };

    const handlePayoutMatchFailed = (message?: string | null) => {
        const nextMessage =
            message ||
            'Account holder name does not match bank records. Please update your registered name to match the bank account and try again.';
        setErrorMessage(nextMessage);
        setSnackbar({
            type: 'failure',
            message:
                message ||
                'Name mismatch with bank account. Update registered name and retry.',
        });
    };

    const onSubmit = async () => {
        setCtaPressed(true);
        setTimeout(() => setCtaPressed(false), 220);
        setWarningMessage('');
        setErrorMessage('');

        const { valid, errors } = validateAllFields(validationConfig, values, {
            isValidIfsc,
        });
        setValidationErr(errors);
        if (!valid) return;

        try {
            setIsBtnLoading(true);
            trackEvent(LegalDocumentsSegments.TAP_BTN_SUBMIT, {
                page: 'legalBankDocuments',
                partner: partnerId,
                product: 'electronics',
                sub_product_name: 'pos',
                phone: userDetails.phone,
                city_name: userDetails.pincode,
                unique_id: userDetails.user_id,
                platform: 'mweb',
                mode,
            });

            const payload = buildSubmitPayload(uiConfig, values);
            const response = await submitFinancialDetails(
                partnerId,
                groupName,
                payload
            );

            if (response.statusCode === 401) {
                router.push('/login');
                return;
            }

            if (response.is_error) {
                const payoutStatus = response.data?.payout_match?.status;
                if (payoutStatus === 'failed') {
                    handlePayoutMatchFailed(
                        response.data?.payout_match?.message ||
                            response.error_message
                    );
                    return;
                }
                // Genie 400 name conflict (dealership ≠ beneficiary) and other errors
                setErrorMessage(response.error_message || '');
                setSnackbar({
                    type: 'failure',
                    message:
                        response.error_message || 'Oops. something went wrong.',
                });
                return;
            }

            const payoutMatch = response.data?.payout_match;
            if (payoutMatch?.status === 'failed') {
                handlePayoutMatchFailed(
                    payoutMatch.message || response.data?.message
                );
                return;
            }
            clearDraftStorage(partnerId, groupName);

            if (payoutMatch?.status === 'partial') {
                const message =
                    payoutMatch.message ||
                    response.data?.message ||
                    'Details saved with a partial name match. Please verify beneficiary details.';
                // Keep legalDetailsUploadedStatus set so this screen stays mounted
                // while the partial-match modal is open (null would unmount the page).
                setWarningMessage(message);
                setErrorMessage('');
                setAwaitingPartialContinue(true);
                return;
            }

            // completed | skipped (fuzzy disabled) → success, no warn
            setLegalDetailsUploadedStatus(null);
            setSnackbar({
                type: 'success',
                message:
                    response.data?.message ||
                    'Document details has been submitted successfully!',
            });
            redirectToDashboard();
        } finally {
            setIsBtnLoading(false);
        }
    };

    const onContinueAfterPartial = () => {
        setAwaitingPartialContinue(false);
        setLegalDetailsUploadedStatus(null);
        redirectToDashboard();
    };

    const onClickTnc = () => {
        setShowTnc(false);
        trackEvent(LegalDocumentsSegments.TAP_BTN_TNC, {
            page: 'legalBankDocuments',
            partner: partnerId,
            product: 'electronics',
            sub_product_name: 'pos',
            phone: userDetails.phone,
            city_name: userDetails.pincode,
            unique_id: userDetails.user_id,
            platform: 'mweb',
        });
    };

    if (!legalDetailsUploadedStatus) {
        return <></>;
    }

    if (showTnc) {
        return (
            <Styles.FormGridWrapper
                style={{ padding: '20px', maxWidth: '100%' }}
            >
                <LegalDocumentTnc onClick={onClickTnc} />
            </Styles.FormGridWrapper>
        );
    }

    return (
        <Styles.FormItemWrapper>
            <Styles.FormGridWrapper>
                <Styles.HeaderWrapper>
                    <Header />
                </Styles.HeaderWrapper>
                {isLoading ? (
                    <Styles.LoadingState>Loading details…</Styles.LoadingState>
                ) : (
                    <FinancialDetailsForm
                        mode={mode}
                        description={description}
                        buttonName={buttonName}
                        uiConfig={uiConfig}
                        values={values}
                        validationErr={validationErr}
                        isBtnLoading={isBtnLoading}
                        isBtnDisabled={
                            isBtnDisabled ||
                            isFetchingIfsc ||
                            awaitingPartialContinue
                        }
                        isFetchingIfsc={isFetchingIfsc}
                        errorMessage={errorMessage}
                        ctaPressed={ctaPressed}
                        onChangeValue={onChangeValue}
                        onBlurField={onBlurField}
                        onSelectFile={onSelectFile}
                        onDeleteFile={onDeleteFile}
                        onClickTncLink={() => setShowTnc(true)}
                        onSubmit={onSubmit}
                    />
                )}
            </Styles.FormGridWrapper>
            <Modal
                show={awaitingPartialContinue}
                urlhash={''}
                onHide={() => {}}
                padding={'24px'}
                maxWidth={'500px'}
                wrapperStyles={`border-radius: 16px 16px 0px 0px;`}
                showOverlayBackground={true}
                hideCloseButton={true}
                disableOverlayClose={true}
            >
                <Styles.PartialModalContent>
                    <Styles.PartialModalTitle>
                        Name match warning
                    </Styles.PartialModalTitle>
                    <Styles.PartialModalText>
                        {warningMessage ||
                            'Details saved with a partial name match. Please verify beneficiary details.'}
                    </Styles.PartialModalText>
                    <Styles.PartialModalHint>
                        You can continue to the dashboard after reviewing this
                        warning.
                    </Styles.PartialModalHint>
                    <Styles.PartialModalActions>
                        <PrimaryButton onClick={onContinueAfterPartial}>
                            Continue
                        </PrimaryButton>
                    </Styles.PartialModalActions>
                </Styles.PartialModalContent>
            </Modal>
        </Styles.FormItemWrapper>
    );
}

export default LegalDocumentsUpload;
